// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which to-dos of a checklist brief (the morning brief) are ticked off.
// The server keeps them per item so a tick on the phone shows on the desktop;
// this store fetches each card's ticks when it appears, refreshes them when
// the card comes back after a while, and ticks optimistically.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, observable, runInAction} from 'mobx';

/** A card seen again after this long asks the server again, in case another device ticked something. */
const STALE_MS = 30_000;
const EMPTY: ReadonlySet<number> = new Set();

function clean(value: unknown): Array<number> {
	return Array.isArray(value) ? value.filter((entry): entry is number => Number.isInteger(entry) && entry >= 0) : [];
}

class BriefChecklistStore {
	done = observable.map<string, ReadonlySet<number>>();
	private fetchedAt = new Map<string, number>();
	private inFlight = new Set<string>();
	/** Items with a tick request pending: a fetch that raced it must not undo the tick. */
	private writing = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'fetchedAt' | 'inFlight' | 'writing'>(this, {
			fetchedAt: false,
			inFlight: false,
			writing: false,
		});
	}

	get(itemId: string): ReadonlySet<number> {
		return this.done.get(itemId) ?? EMPTY;
	}

	/** Home's list already carries the ticks; no need to ask per card. */
	seed(itemId: string, done: unknown, now = Date.now()): void {
		if (this.writing.has(itemId)) return;
		this.done.set(itemId, new Set(clean(done)));
		this.fetchedAt.set(itemId, now);
	}

	/** Best effort: a failed request leaves the card as it was. */
	ensure(itemId: string, now = Date.now()): void {
		if (this.inFlight.has(itemId)) return;
		const at = this.fetchedAt.get(itemId);
		if (at != null && now - at < STALE_MS) return;
		this.inFlight.add(itemId);
		this.fetchedAt.set(itemId, now);
		void http
			// Not 'silent': that hands back a 429's body as if it were the list, unticking everything.
			.get<{done?: unknown}>(Endpoints.KONVO_ASSISTANT_ENGINE_TODOS(itemId), RETRY_RATE_LIMITS)
			.then((response) => {
				if (this.writing.has(itemId)) return;
				runInAction(() => this.done.set(itemId, new Set(clean(response.body?.done))));
			})
			.catch(() => undefined)
			.finally(() => this.inFlight.delete(itemId));
	}

	toggle(itemId: string, index: number): void {
		const next = new Set(this.get(itemId));
		const done = !next.has(index);
		if (done) next.add(index);
		else next.delete(index);
		this.done.set(itemId, next);
		this.writing.add(itemId);
		// The optimistic set is already right; only a failure puts this tick back.
		void http
			.put(Endpoints.KONVO_ASSISTANT_ENGINE_TODO(itemId, index), {body: {done}, mode: 'silent'})
			.catch(() =>
				runInAction(() => {
					const current = new Set(this.get(itemId));
					if (done) current.delete(index);
					else current.add(index);
					this.done.set(itemId, current);
				}),
			)
			.finally(() => {
				this.writing.delete(itemId);
				this.fetchedAt.set(itemId, Date.now());
			});
	}
}

export default new BriefChecklistStore();
